/**
 * Filter selectedMentionUser down to only the mentions still present
 * as entities in the editor content. This prevents deleted @mentions
 * from appearing in the notify list at send time.
 */
export function getActiveMentions(contentState, selectedMentionUser) {
  if (!selectedMentionUser || !selectedMentionUser.length) {
    return [];
  }

  const idSet = new Set();

  contentState.getBlocksAsArray().forEach((block) => {
    block.getCharacterList().forEach((character) => {
      const entityKey = character.getEntity();
      if (entityKey === null) {
        return;
      }
      const entity = contentState.getEntity(entityKey);
      if (!entity.getType().toLowerCase().endsWith("mention")) {
        return;
      }
      const mention = entity.getData().mention;
      if (!mention) {
        return;
      }
      const id = mention.get ? mention.get("id") : mention.id;
      if (id != null) {
        idSet.add(String(id));
      }
    });
  });

  return selectedMentionUser.filter((u) => idSet.has(String(u.id)));
}

export const EVERYONE_MENTION_ID = "everyone";

export const EVERYONE_MENTION = {
  id: EVERYONE_MENTION_ID,
  name: "everyone",
  firstName: "everyone",
  lastName: "",
  color: "#6c757d",
};

/**
 * Build the mention user id list from selected mentions.
 * When @everyone is selected, expands to all company user ids except the sender.
 * Returns null when no mentions are selected (preserves existing behavior).
 */
export function getMentionUserIds(selectedMentionUser, companyUsers, currentUserId) {
  if (!selectedMentionUser || !selectedMentionUser.length) {
    return null;
  }

  const hasEveryone = selectedMentionUser.some(
    (item) => item.id === EVERYONE_MENTION_ID
  );

  if (!hasEveryone) {
    return selectedMentionUser.map((item) => item.id);
  }

  // Expand @everyone to all company user ids, excluding the current user
  const users = companyUsers || [];
  const allIds = users
    .filter((user) => String(user.id) !== String(currentUserId))
    .map((user) => user.id);

  // Also include any individually-picked non-everyone mentions, then dedupe
  const individualIds = selectedMentionUser
    .filter((item) => item.id !== EVERYONE_MENTION_ID)
    .map((item) => item.id);

  const seen = new Set();
  const deduped = [];
  for (const id of [...allIds, ...individualIds]) {
    const key = String(id);
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(id);
    }
  }

  return deduped;
}

/**
 * Build mention user ids from an array of mention name strings (the edit path).
 * Resolves each name to a company user by firstName. For "everyone", expands to
 * all company user ids except the current user. Returns parseFloat'd numeric ids.
 */
export function getMentionUserIdsFromNames(names, companyUsers, currentUserId) {
  if (!names || !names.length) {
    return [];
  }

  const users = companyUsers || [];
  const seen = new Set();
  const result = [];

  const addId = (id) => {
    const key = String(id);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(id);
    }
  };

  for (const name of names) {
    if (name === EVERYONE_MENTION_ID) {
      for (const user of users) {
        if (String(user.id) !== String(currentUserId)) {
          addId(parseFloat(user.id));
        }
      }
      continue;
    }

    const found = users.find((item) => item.firstName === name);
    if (found) {
      addId(parseFloat(found.id));
    }
  }

  return result;
}
